import React from 'react';
import styled from 'styled-components';
import { Table } from 'rbx';
import gql from 'graphql-tag';
import swal from 'sweetalert';

import { useMutation } from '@apollo/react-hooks';
import { useStoreState } from 'easy-peasy';
import { isNil } from 'lodash';
import { toast } from 'react-toastify';

import { Table as CustomTable, Box } from '../../../components/elements';

import List from './List';
import AddEditForm from './AddEditForm';
import client from '../../../utils/apolloClient';

const Container = styled.div`
  .table-container {
    min-height: 30rem;
  }
`;

const createCmsAclMutation = gql`
  mutation createCmsAclMutation($input: CreateCmsAclInput) {
    create_cms_acl(input: $input) {
      acl_id
      api_actions {
        api_action_id
        name
      }
      description
      name
      error {
        code
        description
      }
      status
      page_actions {
        page_action_id
        name
        status
      }
    }
  }
`;

const updateCmsAclMutation = gql`
  mutation updateCmsAclMutation($input: UpdateCmsAclInput) {
    update_cms_acl(input: $input) {
      acl_id
      api_actions {
        api_action_id
        name
      }
      description
      name
      error {
        code
        description
      }
      status
      page_actions {
        page_action_id
        name
        status
      }
    }
  }
`;

const deleteCmsAclMutation = gql`
  mutation deleteCmsAclMutation($input: DeleteCmsAclInput) {
    delete_cms_acl(input: $input) {
      acl_id
      api_actions {
        api_action_id
        name
      }
      description
      name
      error {
        code
        description
      }
      status
      page_actions {
        page_action_id
        name
        status
      }
    }
  }
`;

const Details = ({ acls, activeModalProp, refetch }) => {
  const { userId } = useStoreState(state => state.auth);

  const {
    selectedAcl,
    setSelectedAcl,
    isActiveModal,
    setIsActiveModal,
  } = activeModalProp;

  const [createAcl, { loading }] = useMutation(createCmsAclMutation, {
    client: client.clientPrivate,
    onCompleted: ({ create_cms_acl }) => {
      if (!isNil(create_cms_acl.error)) {
        create_cms_acl.error.map(item => toast.error(item.description));
      } else {
        swal('Successfully added acl!').then(() => {
          setIsActiveModal(false);
          refetch();
        });
      }
    },
  });

  const [updateAcl, { loading: loading2 }] = useMutation(updateCmsAclMutation, {
    client: client.clientPrivate,
    onCompleted: ({ update_cms_acl }) => {
      if (!isNil(update_cms_acl.error)) {
        update_cms_acl.error.map(item => toast.error(item.description));
      } else {
        swal('Successfully updated acl!').then(() => {
          setIsActiveModal(false);
          refetch();
          setSelectedAcl(null);
        });
      }
    },
  });

  const [deleteAcl, { loading: isDeleting }] = useMutation(
    deleteCmsAclMutation,
    {
      client: client.clientPrivate,
      onCompleted: ({ delete_cms_acl }) => {
        if (!isNil(delete_cms_acl.error)) {
          delete_cms_acl.error.map(item => toast.error(item.description));
        } else {
          swal('Successfully deleted acl!').then(() => {
            refetch();
            setSelectedAcl(null);
          });
        }
      },
    },
  );

  return (
    <React.Fragment>
      <Box>
        <Container>
          <CustomTable>
            <Table.Head>
              <Table.Row>
                <Table.Heading>Name</Table.Heading>
                <Table.Heading>Description</Table.Heading>
                <Table.Heading>Actions</Table.Heading>
              </Table.Row>
            </Table.Head>
            <Table.Body>
              <List
                acls={acls}
                onEdit={value => {
                  setSelectedAcl(value);
                  setIsActiveModal(true);
                }}
                onDelete={async value => {
                  deleteAcl({
                    variables: {
                      input: {
                        acl_id: value?.acl_id,
                        user_id: userId,
                      },
                    },
                  });
                }}
                isDeleting={isDeleting}
              />
            </Table.Body>
          </CustomTable>
        </Container>
      </Box>
      {isActiveModal && (
        <AddEditForm
          acl={selectedAcl}
          loading={loading || loading2}
          isActive={isActiveModal}
          onClose={() => setIsActiveModal(false)}
          onSubmit={values => {
            if (selectedAcl) {
              updateAcl({
                variables: {
                  input: {
                    acl_id: selectedAcl?.acl_id,
                    user_id: userId,
                    name: values.name,
                    description: values.description,
                    api_action_ids: values.api_actions,
                    page_action_ids: values.page_actions,
                  },
                },
              });
            } else {
              createAcl({
                variables: {
                  input: {
                    user_id: userId,
                    name: values.name,
                    description: values.description,
                    api_action_ids: values.api_actions,
                    page_action_ids: values.page_actions,
                  },
                },
              });
            }
          }}
        />
      )}
    </React.Fragment>
  );
};

export default Details;
